import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Grid, InputLabel, OutlinedInput, Typography, Switch, IconButton } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { gridSpacing } from 'utils/constants';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import DeleteConfirmationDialog from 'components/Management/DeleteConfirmationDialog';
import ApiHelper from 'utils/apiHelper';
import { validateInputValue, validateNumber } from 'utils/commonHelpers';
import EditScriptDialog from './EditScriptDialog';

const AdvancedSettings = () => {
    const { userInfo } = useSelector((state) => state.auth);
    const [advancedSettingsData, setAdvancedSettingsData] = useState('');
    const [deleteScriptId, setDeleteScriptId] = useState();
    const [selectedScriptData, setSelectedScriptData] = useState();
    const [showEditScriptDialog, setShowEditScriptDialog] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);

    useEffect(() => {
        getAdvancedSettingsData();
    }, []);

    const getAdvancedSettingsData = () => {
        ApiHelper.get('/api/settings/advanced_settings/')
            .then((res) => {
                setAdvancedSettingsData(res.data);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error(err.response.data.message);
                }
            });
    };

    const handleChangeSwitchData = (e) => {
        setAdvancedSettingsData({
            ...advancedSettingsData,
            [e.target.name]: e.target.checked
        });
    };

    const handleChangeSettingsData = (e) => {
        if (e.target.value === '') toast.error('Please type the value');
        if (validateNumber(e.target.value, 'integer') === true) {
            setAdvancedSettingsData({
                ...advancedSettingsData,
                [e.target.name]: e.target.value
            });
        } else {
            toast.error('It should be an integer value.');
        }
    };

    const handleSubmit = () => {
        if (
            advancedSettingsData.idleLength === '' ||
            advancedSettingsData.aiLength === '' ||
            advancedSettingsData.chargeMin === '' ||
            advancedSettingsData.preOff === '' ||
            advancedSettingsData.dimmingAmount === '' ||
            advancedSettingsData.inactiveTimeout === ''
        ) {
            toast.error('There are some empty values');
            return true;
        }
        if (
            validateInputValue(advancedSettingsData.dimmingAmount, 'percentage') === false ||
            validateInputValue(advancedSettingsData.chargeMin, 'percentage') === false
        ) {
            toast.error('Percentage value should be between 0 and 100');
            return true;
        }
        ApiHelper.put(`/api/settings/advanced_settings/${advancedSettingsData.id}/`, {
            ai_wake: advancedSettingsData.aiWake,
            ai_audit: advancedSettingsData.aiAudit,
            priority_wake: advancedSettingsData.priorityWake,
            ai_data: advancedSettingsData.aiData,
            idle_length: advancedSettingsData.idleLength,
            ai_length: advancedSettingsData.aiLength,
            charge_min: advancedSettingsData.chargeMin,
            use_off: advancedSettingsData.useOff,
            pre_off: advancedSettingsData.preOff,
            when_charge: advancedSettingsData.whenCharge,
            when_idle: advancedSettingsData.whenIdle,
            demand_reduction: advancedSettingsData.demandReduction,
            dimming: advancedSettingsData.dimmingAmount,
            inactive: advancedSettingsData.inactiveTimeout,
            enable_connections: advancedSettingsData.enableConnections,
            enable_orgapi: advancedSettingsData.enableAPIs,
            enable_autobase: advancedSettingsData.enableAutoBaseline,
            enable_avgbase: advancedSettingsData.useAverageBaseline,
            average_baseline: advancedSettingsData.averageBaseline,
            baseline_length: advancedSettingsData.baselineCaptureLength,
            enable_scripts: advancedSettingsData.enableScripts
        })
            .then(() => {
                toast.success('Successfully updated.');
            })
            .catch(() => {
                toast.error('Failed to update the security settings data');
            });
    };

    const handleDeleteConfirmation = (id) => {
        setShowDeleteConfirmationDialog(true);
        setDeleteScriptId(id);
    };

    const handleClose = () => {
        setShowDeleteConfirmationDialog(false);
    };

    const handleCloseEditScript = () => {
        setShowEditScriptDialog(false);
        getAdvancedSettingsData();
    };

    const handleEditScript = (data) => {
        setSelectedScriptData(data);
        setShowEditScriptDialog(true);
    };

    const handleDelete = () => {
        ApiHelper.delete(`/api/server/scripts/${deleteScriptId}/`)
            .then(() => {
                toast.success('Successfully deleted the script');
                getAdvancedSettingsData();
                handleClose();
            })
            .catch((err) => {
                toast.error(`Fail! ${err.response.data.message}`);
                handleClose();
            });
    };

    if (!advancedSettingsData) {
        return null;
    } else {
        return (
            <MainCard title="Advanced Settings" cardHeaderTitle="System Options" cardHeaderShow={true}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12} sx={{ position: 'relative', mt: 2, mb: 2 }}>
                        <Box sx={{ position: 'absolute', right: '0', top: '0' }}>
                            <Button size="large" color="secondary" variant="contained" onClick={handleSubmit}>
                                Save
                            </Button>
                            <Button onClick={getAdvancedSettingsData}>Cancel</Button>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <SubCard title="Intelligent Update(iUpdate)">
                            <Grid container>
                                <Grid item xs={12} sx={{ display: 'flex', textAlign: 'center', mb: 2 }}>
                                    <InputLabel style={{ marginTop: '10px' }}>Enable iUpdate</InputLabel>
                                    <Switch
                                        color="success"
                                        checked={advancedSettingsData.aiWake}
                                        disabled={!advancedSettingsData.iUpdate}
                                        name="aiWake"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ ml: '20px', display: 'flex', textAlign: 'center', mb: 2 }}>
                                    <InputLabel style={{ marginLeft: '10px', marginTop: '10px', marginRight: '4%' }}>
                                        Get Device Idleness
                                    </InputLabel>
                                    <Switch
                                        color="success"
                                        checked={advancedSettingsData.aiData}
                                        disabled={!advancedSettingsData.aiWake}
                                        name="aiData"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ ml: '52px', display: 'flex', textAlign: 'center', mb: 2 }}>
                                    <Typography style={{ color: 'black' }}>Idleness Length (15 mins)</Typography>
                                    <OutlinedInput
                                        sx={{
                                            width: '50px',
                                            height: '30px',
                                            marginLeft: '50px',
                                            marginTop: '-5px',
                                            textAlign: 'left'
                                        }}
                                        value={advancedSettingsData.idleLength}
                                        disabled={!advancedSettingsData.aiWake || !advancedSettingsData.aiData}
                                        name="idleLength"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ ml: '52px', display: 'flex', textAlign: 'center', mb: 2 }}>
                                    <Typography style={{ color: 'black' }}>iUpdate History Used (wks)</Typography>
                                    <OutlinedInput
                                        sx={{
                                            width: '50px',
                                            height: '30px',
                                            marginLeft: '42px',
                                            marginTop: '-5px',
                                            textAlign: 'left'
                                        }}
                                        value={advancedSettingsData.aiLength}
                                        disabled={!advancedSettingsData.aiWake || !advancedSettingsData.aiData}
                                        name="aiLength"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ ml: '20px', display: 'flex', textAlign: 'center', mb: 2 }}>
                                    <InputLabel style={{ marginLeft: '10px', marginTop: '10px', marginRight: '3%' }}>
                                        Enable Priority Wake
                                    </InputLabel>
                                    <Switch
                                        color="success"
                                        checked={advancedSettingsData.priorityWake}
                                        disabled={!advancedSettingsData.aiWake}
                                        name="priorityWake"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ ml: '20px', display: 'flex', textAlign: 'left', mb: 1 }}>
                                    <InputLabel style={{ marginLeft: '10px', marginTop: '10px', marginRight: '10%' }}>
                                        Update Devices
                                    </InputLabel>
                                    <Switch
                                        color="success"
                                        checked={advancedSettingsData.aiAudit}
                                        disabled={!advancedSettingsData.aiWake}
                                        name="aiAudit"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ ml: '52px', marginTop: '0px', display: 'flex', textAlign: 'left', mb: 1 }}>
                                    <InputLabel style={{ marginLeft: '10px', marginTop: '10px', marginRight: '10%' }}>
                                        Only update if User is idle
                                    </InputLabel>
                                    <Switch
                                        color="success"
                                        checked={advancedSettingsData.whenIdle}
                                        disabled={!advancedSettingsData.aiWake || !advancedSettingsData.aiAudit}
                                        name="whenIdle"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ ml: '52px', marginTop: '0px', display: 'flex', textAlign: 'left', mb: 2 }}>
                                    <InputLabel style={{ marginLeft: '10px', marginTop: '10px', marginRight: '15%' }}>
                                        Update when charging
                                    </InputLabel>
                                    <Switch
                                        color="success"
                                        checked={advancedSettingsData.whenCharge}
                                        disabled={!advancedSettingsData.aiWake || !advancedSettingsData.aiAudit}
                                        name="whenCharge"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ ml: '60px', marginTop: '10px', display: 'flex', textAlign: 'left', mb: 1 }}>
                                    <Typography style={{ color: 'black' }}>Minimum Charge Level (%)</Typography>
                                    <OutlinedInput
                                        sx={{ width: '50px', height: '30px', marginLeft: '40px', marginTop: '-5px', textAlign: 'left' }}
                                        value={advancedSettingsData.chargeMin}
                                        disabled={!advancedSettingsData.aiWake || !advancedSettingsData.aiAudit}
                                        name="chargeMin"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ ml: '52px', marginTop: '0px', display: 'flex', textAlign: 'left', mb: 2 }}>
                                    <InputLabel style={{ marginLeft: '10px', marginTop: '10px', marginRight: '22%' }}>
                                        Update When OFF
                                    </InputLabel>
                                    <Switch
                                        color="success"
                                        checked={advancedSettingsData.useOff}
                                        disabled={!advancedSettingsData.aiWake || !advancedSettingsData.aiAudit}
                                        name="useOff"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ ml: '62px', marginTop: '0px', display: 'flex', textAlign: 'left', mb: 2 }}>
                                    <Typography style={{ color: 'black' }}>OFF buffer required (mins)</Typography>
                                    <OutlinedInput
                                        sx={{ width: '50px', height: '30px', marginLeft: '40px', marginTop: '-5px', textAlign: 'left' }}
                                        value={advancedSettingsData.preOff}
                                        disabled={!advancedSettingsData.aiWake || !advancedSettingsData.aiAudit}
                                        name="preOff"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ mt: 3 }}>
                                    <Button
                                        // onClick={() => setShowRefreshDialog(true)}
                                        // startIcon={<AddIcon />}
                                        size="large"
                                        color="primary"
                                        variant="contained"
                                    >
                                        Refresh Token
                                    </Button>
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                    <Grid item xs={3}>
                        <SubCard title="Demand Reduction">
                            <Grid container>
                                <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Enable Demand Reduction</InputLabel>
                                    <Switch
                                        color="success"
                                        // id="passwordSettings"
                                        checked={advancedSettingsData.demandReduction}
                                        disabled={!advancedSettingsData.demand}
                                        name="demandReduction"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <Typography>Display Dimming Amount (%)</Typography>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={advancedSettingsData.dimmingAmount}
                                        disabled={!advancedSettingsData.demandReduction}
                                        name="dimmingAmount"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Minimum Inactivity Timeout (mins)</InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={advancedSettingsData.inactiveTimeout}
                                        disabled={!advancedSettingsData.demandReduction}
                                        name="inactiveTimeout"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                    <Grid item xs={3}>
                        <SubCard title="Raptor Appliance Connections">
                            <Grid container>
                                <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Enable Appliance Connections</InputLabel>
                                    <Switch
                                        color="success"
                                        checked={advancedSettingsData.enableConnections}
                                        disabled={!advancedSettingsData.appConnect}
                                        name="enableConnections"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                            </Grid>
                        </SubCard>
                        <SubCard sx={{ padding: '20px, 0', mt: 2 }} title="Raptor API Connections">
                            <Grid container>
                                <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Enable API Connections</InputLabel>
                                    <Switch
                                        color="success"
                                        checked={advancedSettingsData.enableAPIs}
                                        name="enableAPIs"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                            </Grid>
                        </SubCard>
                        {userInfo.admin_permissions.root_admin && (
                            <SubCard sx={{ padding: '20px, 0', mt: 2 }} title="Baseline Settings">
                                <Grid container>
                                    <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                        <InputLabel>Enable Auto-Baseline</InputLabel>
                                        <Switch
                                            color="success"
                                            checked={advancedSettingsData.enableAutoBaseline}
                                            name="enableAutoBaseline"
                                            onChange={handleChangeSwitchData}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{ padding: '0 5px', mt: 2, mb: 2 }}>
                                        <InputLabel>Use Average Baseline</InputLabel>
                                        <Switch
                                            color="success"
                                            checked={advancedSettingsData.useAverageBaseline}
                                            name="useAverageBaseline"
                                            onChange={handleChangeSwitchData}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{ display: 'flex', textAlign: 'center' }}>
                                        <Typography style={{ color: 'black' }}>Baseline Capture Length(days)</Typography>
                                        <OutlinedInput
                                            sx={{
                                                width: '50px',
                                                height: '30px',
                                                marginLeft: '50px',
                                                marginTop: '-5px',
                                                textAlign: 'left'
                                            }}
                                            value={advancedSettingsData.baselineCaptureLength}
                                            name="baselineCaptureLength"
                                            onChange={handleChangeSettingsData}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                        <Typography>Average Baseline</Typography>
                                        <OutlinedInput
                                            sx={{ width: '100%' }}
                                            value={advancedSettingsData.averageBaseline}
                                            name="averageBaseline"
                                            onChange={handleChangeSettingsData}
                                        />
                                    </Grid>
                                </Grid>
                            </SubCard>
                        )}
                    </Grid>
                    <Grid item xs={3}>
                        <SubCard title="Script Management">
                            <Grid container>
                                <Grid item xs={12} sx={{ display: 'flex', textAlign: 'center', mt: 2 }}>
                                    <InputLabel style={{ marginTop: '10px' }}>Enable Scripts</InputLabel>
                                    <Switch
                                        color="success"
                                        checked={advancedSettingsData.enableScripts}
                                        name="enableScripts"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <Typography>Scripts</Typography>
                                    {advancedSettingsData.scripts.map((data, key) => (
                                        <Box key={key} sx={{ display: 'flex' }}>
                                            <IconButton
                                                size="small"
                                                disabled={!advancedSettingsData.enableScripts}
                                                onClick={() => handleEditScript(data)}
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                disabled={!advancedSettingsData.enableScripts}
                                                onClick={() => handleDeleteConfirmation(data.id)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                            <Typography sx={{ marginTop: '6px' }}>{`- ${data.name}`}</Typography>
                                        </Box>
                                    ))}
                                    {showEditScriptDialog && (
                                        <EditScriptDialog
                                            onCloseCancel={() => setShowEditScriptDialog(false)}
                                            onClose={handleCloseEditScript}
                                            data={selectedScriptData}
                                        />
                                    )}
                                    {showDeleteConfirmationDialog && (
                                        <DeleteConfirmationDialog onClose={handleClose} onYes={handleDelete} name="script" />
                                    )}
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default AdvancedSettings;
